// src/lib/notificationsService.ts
//
// Service de notifications de relance pour le tracker de candidatures.
//
// Approche : Notification API native (pas de backend push). Quand l'app
// est ouverte (onglet ou PWA), on check les candidatures stale et on push
// une notif système. Pour les vraies push même app fermée, il faudrait
// VAPID + Web Push backend — reporté en C3+ si besoin réel.
//
// Stratégies de relance :
// - Candidature en statut actif (applied/phone/interview) sans maj > 10j
// - Pas de spam : on stocke la date de dernière notif par candidature
//   et on ne re-notifie qu'après 7 jours minimum

const NOTIFY_KEY = 'recruteursio:notifications:last-sent:v1';
const NOTIFY_PERMISSION_ASKED = 'recruteursio:notifications:permission-asked';
const STALE_DAYS = 10;
const RENOTIFY_DAYS = 7;

interface NotificationHistory {
  [applicationId: string]: string; // ISO date of last notif sent
}

export interface NotifiableApp {
  id: string;
  company: string;
  role: string;
  status: string;
  lastUpdate: string;
}

// ─── Permission management ──────────────────────────────────────────────────

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function hasAskedPermission(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(NOTIFY_PERMISSION_ASKED) === '1';
}

export function markPermissionAsked(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(NOTIFY_PERMISSION_ASKED, '1');
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }
  markPermissionAsked();
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

// ─── History (anti-spam) ────────────────────────────────────────────────────

function loadHistory(): NotificationHistory {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(NOTIFY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveHistory(history: NotificationHistory): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NOTIFY_KEY, JSON.stringify(history));
  } catch {
    /* quota — silencieux */
  }
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

// ─── Détection candidatures stale ───────────────────────────────────────────

const ACTIVE_STATUSES = new Set(['applied', 'phone', 'interview', 'offer']);

export function findStaleApplications(apps: NotifiableApp[]): NotifiableApp[] {
  return apps
    .filter((a) => ACTIVE_STATUSES.has(a.status))
    .filter((a) => daysSince(a.lastUpdate) >= STALE_DAYS);
}

// ─── Émission de notifications ──────────────────────────────────────────────

/**
 * Notifie les candidatures stale qui n'ont pas été notifiées récemment.
 * Retourne le nombre de notifications envoyées.
 */
export async function notifyStaleApplications(apps: NotifiableApp[]): Promise<number> {
  if (getNotificationPermission() !== 'granted') return 0;

  const history = loadHistory();
  const stale = findStaleApplications(apps);
  let sent = 0;

  for (const app of stale) {
    const lastNotified = history[app.id];
    if (lastNotified && daysSince(lastNotified) < RENOTIFY_DAYS) {
      continue; // déjà notifié récemment, on respecte le cooldown
    }
    sendNotification(app);
    history[app.id] = new Date().toISOString();
    sent += 1;
  }

  if (sent > 0) saveHistory(history);
  return sent;
}

function sendNotification(app: NotifiableApp): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  const days = daysSince(app.lastUpdate);

  const title = `💼 ${app.company}`;
  const body = `${app.role} — ${days} jours sans nouvelle. Une relance polie augmente tes chances de 30 %.`;

  // Utiliser ServiceWorkerRegistration.showNotification si dispo (PWA installée)
  // sinon fallback sur new Notification (onglet ouvert)
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready
      .then((reg) => {
        reg.showNotification(title, {
          body,
          icon: '/icon.svg',
          badge: '/icon.svg',
          tag: `relance-${app.id}`,
          renotify: false,
          requireInteraction: false,
          data: { applicationId: app.id, url: '/ressources/outils/suivi-candidature' },
        });
      })
      .catch(() => {
        // Fallback si SW pas prêt
        new Notification(title, { body, icon: '/icon.svg', tag: `relance-${app.id}` });
      });
  } else {
    try {
      new Notification(title, { body, icon: '/icon.svg', tag: `relance-${app.id}` });
    } catch {
      /* permission révoquée entre-temps, on ignore */
    }
  }
}

/**
 * Test notification — utilisé pour onboarding "Activer les notifs" :
 * envoie une notif de bienvenue pour confirmer que ça marche.
 */
export function sendTestNotification(): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (getNotificationPermission() !== 'granted') return;

  const title = '🔔 Notifications activées';
  const body = 'On te rappellera quand une candidature traîne sans nouvelle depuis 10 jours.';

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((reg) => {
      reg.showNotification(title, { body, icon: '/icon.svg', badge: '/icon.svg' });
    }).catch(() => {
      new Notification(title, { body, icon: '/icon.svg' });
    });
  } else {
    try {
      new Notification(title, { body, icon: '/icon.svg' });
    } catch {
      /* ignore */
    }
  }
}
